import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import History from 'src/components/History';
import { ConstantsCodesObjectType } from 'src/types/constantsCodes';
import {
    historyInsertCountriesUpdates,
    historyProductFilesUpdates,
    historyProductRelationsUpdates,
    historyProductSetUpdates,
    historyEansUpdates,
    historyUpdates,
    historyUpdatesSkladonUser,
    historyCreateHiddenKeys,
} from './constants';
import { ProductChangelogType } from 'src/types/productDetail';
import { countryOptionsCodesObject } from 'src/utils/countryOptions';
import { getProductAttachmentFileTypeCodesObject } from 'src/utils/productAttachmentFileTypes';
import { getProductSourceSysCodesObject } from 'src/utils/productSourceSysSelectOptions';
import { DeleteIcon } from 'src/components/SkladonIcon/SmallIcons';
import { styled } from '@mui/material/styles';
import translate from '@translate';
import { RootStoreType } from 'src/types/store';
import { resolveLinkedEshopName } from './functions';

/** Changelog stores boolean fields as `true` / `false`, so codes must be keyed the same way. */
const getBooleanHistoryCodesObject = (): ConstantsCodesObjectType => ({
    true: { value: true, label: translate('utils.boolCodes.yes') },
    false: { value: false, label: translate('utils.boolCodes.no') },
});

const StyledDeleteIcon = styled('span')({
    '& svg': {
        width: '20px',
        height: '20px',
        marginTop: '2px',
    },
});
export interface HistoryPropsType {
    changelog: ProductChangelogType | null;
    productTypeCodesObject: ConstantsCodesObjectType;
    productInsetTypeCodesObject: ConstantsCodesObjectType;
    boolNumberCodesObject: ConstantsCodesObjectType;
    activeNumberCodesObject: ConstantsCodesObjectType;
    expeditionMethodCodesObject: ConstantsCodesObjectType;
    currencyCodesObject: ConstantsCodesObjectType;
    productRelationCodesObject: ConstantsCodesObjectType;
    scanImeiSnCodesObject: ConstantsCodesObjectType;
    batteryTypeCodesObject: ConstantsCodesObjectType;
    insertByOrderTypeCodesObject: ConstantsCodesObjectType;
    skladonUser?: number;
}

const HistoryBlock: FC<HistoryPropsType> = (props) => {
    const providers = useSelector((state: RootStoreType) => state.user.data?.providers);

    return (
        <History
            updates={props.changelog?.updates}
            updateKeys={props.skladonUser ? historyUpdatesSkladonUser : historyUpdates}
            keyCodes={{
                PRODUCT_TYPE: props.productTypeCodesObject,
                INSERT_TYPE: props.productInsetTypeCodesObject,
                INSERT_BY_ORDER_TYPE: props.insertByOrderTypeCodesObject,
                AGECHECK: props.boolNumberCodesObject,
                QUANTITY_CONVERSION: props.boolNumberCodesObject,
                GLOBAL_PRODUCT: props.boolNumberCodesObject,
                SCAN_IMEI_SN_TYPE: props.scanImeiSnCodesObject,
                BATTERY_TYPE: props.batteryTypeCodesObject,
                EXP_METHOD: props.expeditionMethodCodesObject,
                UNIT_PRICE: props.currencyCodesObject,
                ORIGIN_COUNTRY: countryOptionsCodesObject,
                ACTIVE: props.activeNumberCodesObject,
                IS_DELETED: props.activeNumberCodesObject,
                EAN_LOCKED_BY_DCIX: props.boolNumberCodesObject,
                SOURCE_SYS: getProductSourceSysCodesObject(),
                ATTACHED_IDENTIFIER: getBooleanHistoryCodesObject(),
            }}
            keyCallbacks={{
                LINKED_ESHOP: (value) => resolveLinkedEshopName(value, providers),
            }}
            updateItemsKeys={{
                PRODUCT_INSERT_COUNTRIES: { itemKeys: historyInsertCountriesUpdates },
                PRODUCT_RELATIONS_2: { itemKeys: historyProductRelationsUpdates },
                PRODUCT_SET: { itemKeys: historyProductSetUpdates, requiredItems: ['PRODUCT_SKU'] },
                FILES: { itemKeys: historyProductFilesUpdates, requiredItems: ['ORIGINAL_FILE_NAME'] },
                EANS: { itemKeys: historyEansUpdates, requiredItems: ['ean'] },
            }}
            keyItemsCodes={{
                PRODUCT_INSERT_COUNTRIES: {
                    COUNTRY: countryOptionsCodesObject,
                },
                PRODUCT_RELATIONS_2: {
                    TYPE: props.productRelationCodesObject,
                },
                FILES: {
                    TYPE: getProductAttachmentFileTypeCodesObject(),
                },
                EANS: {
                    active: getBooleanHistoryCodesObject(),
                },
            }}
            translatePrefix="productDetail"
            createHiddenKeys={historyCreateHiddenKeys}
            customIconFce={(row) => {
                if (row.message === 'deleteProduct') {
                    return (
                        <StyledDeleteIcon>
                            <DeleteIcon />
                        </StyledDeleteIcon>
                    );
                }
                return null;
            }}
        />
    );
};

export default HistoryBlock;
